import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { doc, setDoc, onSnapshot, collection, query, where, orderBy, limit, getDocs, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import { useAuth } from './AuthContext';
import { signOut } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import { DocumentMenu } from './components/DocumentMenu';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const LAST_OPENED_KEY = 'sumsum_last_opened_doc';

// Utility: Format number with spaces as thousand separators (e.g., 1 000 000)
const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';

  // Round to 2 decimal places and remove trailing zeros
  const rounded = Number.isInteger(num) ? num : parseFloat(num.toFixed(2));
  const parts = rounded.toString().split('.');

  // Add space separators for thousands
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  // Handle decimals - remove trailing zeros
  if (parts[1]) {
    const decimal = parts[1].replace(/0+$/, '');
    return decimal ? `${integerPart},${decimal}` : integerPart;
  }

  return integerPart;
};

// Utility: Parse number string (handles spaces and commas)
const parseNumber = (str) => {
  if (!str || str.trim() === '') return 0;
  const cleaned = str.replace(/\s/g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

// Generate unique ID
const generateId = () => Date.now() + Math.random();

// Sum Element Component
const SumElement = ({
  element,
  index,
  onUpdate,
  onDelete,
  onEnterFromName,
  onEnterFromValue,
  onBackspace,
  onArrowDown,
  onArrowUp,
  nameRef,
  valueRef,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging,
}) => {
  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnterFromName(index);
    } else if (e.key === 'Backspace' && element.name === '' && element.value === '') {
      e.preventDefault();
      onBackspace(index);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      onArrowDown(index, 'name');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      onArrowUp(index, 'name');
    }
  };

  const handleValueKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnterFromValue(index);
    } else if (e.key === 'Backspace' && element.value === '' && element.name === '') {
      e.preventDefault();
      onBackspace(index);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      onArrowDown(index, 'value');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      onArrowUp(index, 'value');
    }
  };

  // Format the value when leaving the field
  const handleValueBlur = () => {
    const num = parseNumber(element.value);
    if (num !== 0 || element.value !== '') {
      onUpdate(index, 'value', num === 0 ? '' : formatNumber(num));
    }
  };

  return (
    <div
      className={`pe-2 flex items-center rounded transition-all ${
        isDragging ? 'opacity-50 bg-white/5' : 'hover:bg-white/5'
      }`}
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, index)}
    >
      {/* Name input */}
      <input
        ref={nameRef}
        type="text"
        value={element.name}
        onChange={(e) => onUpdate(index, 'name', e.target.value)}
        onKeyDown={handleNameKeyDown}
        placeholder="Add expense..."
        className="flex-1 min-w-0 bg-transparent text-white placeholder-gray-500 outline-none border-b border-transparent py-1"
      />

      {/* Value input */}
      <input
        ref={valueRef}
        type="text"
        value={element.value}
        onChange={(e) => onUpdate(index, 'value', e.target.value)}
        onKeyDown={handleValueKeyDown}
        onBlur={handleValueBlur}
        placeholder="0"
        className="w-28 shrink-0 bg-transparent text-[#9799CA] text-right placeholder-gray-600 outline-none border-b border-transparent py-1"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      />
    </div>
  );
};

// Sum Block Component (Toggle List)
const SumBlock = forwardRef(({
  block,
  blockIndex,
  onUpdateBlock,
  onDeleteBlock,
  onEnterFromTitle,
  onBackspaceFromTitle,
  onArrowDownFromTitle,
  onArrowUpFromTitle,
  onArrowDownFromElement,
  onArrowUpFromElement,
  titleRef,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging,
  isOpen: externalIsOpen,
  onToggle,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const elementNameRefs = useRef([]);
  const elementValueRefs = useRef([]);
  const [draggedElementIndex, setDraggedElementIndex] = useState(null);

  // Expose element refs to parent
  useImperativeHandle(ref, () => ({
    getElementNameRefs: () => elementNameRefs.current,
    getElementValueRefs: () => elementValueRefs.current,
  }));

  // Ensure refs array is properly sized
  useEffect(() => {
    elementNameRefs.current = elementNameRefs.current.slice(0, block.elements.length);
    elementValueRefs.current = elementValueRefs.current.slice(0, block.elements.length);
  }, [block.elements.length]);

  // Sync external isOpen state
  useEffect(() => {
    if (externalIsOpen !== undefined) {
      setIsOpen(externalIsOpen);
    }
  }, [externalIsOpen]);

  // Calculate sum of all elements in this block
  const blockSum = block.elements.reduce((sum, el) => sum + parseNumber(el.value), 0);

  // Update element
  const handleUpdateElement = (index, field, value) => {
    const newElements = [...block.elements];
    newElements[index] = { ...newElements[index], [field]: value };
    onUpdateBlock(blockIndex, { ...block, elements: newElements });
  };


  // Delete element
  const handleDeleteElement = (index) => {
    if (block.elements.length === 1) {
      // Keep at least one element, just clear it
      const newElements = [{ id: generateId(), name: '', value: '' }];
      onUpdateBlock(blockIndex, { ...block, elements: newElements });
      return;
    }
    const newElements = block.elements.filter((_, i) => i !== index);
    onUpdateBlock(blockIndex, { ...block, elements: newElements });

    // Focus previous element
    setTimeout(() => {
      const prevIndex = Math.max(0, index - 1);
      elementNameRefs.current[prevIndex]?.focus();
    }, 10);
  };

  // Handle Enter from name field -> move to value field
  const handleEnterFromName = (index) => {
    elementValueRefs.current[index]?.focus();
  };

  // Handle Enter from value field -> create new element
  const handleEnterFromValue = (index) => {
    const newElements = [...block.elements];
    const newElement = { id: generateId(), name: '', value: '' };
    newElements.splice(index + 1, 0, newElement);
    onUpdateBlock(blockIndex, { ...block, elements: newElements });

    // Focus new element's name field after React finishes rendering
    setTimeout(() => {
      const newElementNameRef = elementNameRefs.current[index + 1];
      if (newElementNameRef) {
        newElementNameRef.focus();
      }
    }, 0);
  };

  // Handle backspace on empty element
  const handleBackspaceElement = (index) => {
    handleDeleteElement(index);
  };

  // Handle arrow down from element
  const handleArrowDownFromElement = (elementIndex, field) => {
    if (field === 'name') {
      // From name field, go to value field of same element
      elementValueRefs.current[elementIndex]?.focus();
    } else {
      // From value field, pass to parent to handle navigation to next block/element
      onArrowDownFromElement(blockIndex, elementIndex);
    }
  };

  // Handle arrow up from element
  const handleArrowUpFromElement = (elementIndex, field) => {
    if (field === 'value') {
      // From value field, go to name field of same element
      elementNameRefs.current[elementIndex]?.focus();
    } else {
      // From name field, pass to parent to handle navigation to previous block/element
      onArrowUpFromElement(blockIndex, elementIndex);
    }
  };

  // Handle title keydown
  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnterFromTitle(blockIndex);
    } else if (e.key === 'Backspace' && block.title === '' && block.elements.every(el => el.name === '' && el.value === '')) {
      e.preventDefault();
      onBackspaceFromTitle(blockIndex);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      onArrowDownFromTitle(blockIndex);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      onArrowUpFromTitle(blockIndex);
    } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      if (onToggle) onToggle(blockIndex, newIsOpen);
    }
  };

  // Toggle open/close
  const isTogglingRef = useRef(false);

  const handleToggle = () => {
    isTogglingRef.current = true;
    setIsOpen(!isOpen);
    if (!isOpen && block.elements.length === 0) {
      // Add first element when opening empty block
      const newElements = [{ id: generateId(), name: '', value: '' }];
      onUpdateBlock(blockIndex, { ...block, elements: newElements });
    }
    // Reset flag after a short delay
    setTimeout(() => {
      isTogglingRef.current = false;
    }, 100);
  };

  // Ensure there's always at least one element when opened
  useEffect(() => {
    if (isOpen && block.elements.length === 0) {
      const newElements = [{ id: generateId(), name: '', value: '' }];
      onUpdateBlock(blockIndex, { ...block, elements: newElements });
    }
  }, [isOpen, block.elements.length, blockIndex, onUpdateBlock, block]);

  // Handle blur on title - delete block if empty, has no children, and is closed
  const handleTitleBlur = () => {
    // Don't delete if we're in the process of toggling the block
    if (isTogglingRef.current) {
      return;
    }

    // Only delete if block is closed (not being actively worked on)
    if (isOpen) {
      return;
    }

    const isEmpty = block.title.trim() === '' &&
                    (block.elements.length === 0 ||
                     block.elements.every(el => el.name.trim() === '' && el.value.toString().trim() === ''));
    if (isEmpty) {
      onDeleteBlock(blockIndex);
    }
  };

  // Focus first element when opening (only when isOpen changes, not when elements change)
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current && block.elements.length > 0) {
      setTimeout(() => {
        elementNameRefs.current[0]?.focus();
      }, 50);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, block.elements.length]);

  // Element drag and drop
  const handleElementDragStart = (e, index) => {
    setDraggedElementIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleElementDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleElementDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedElementIndex === null || draggedElementIndex === targetIndex) {
      setDraggedElementIndex(null);
      return;
    }

    const newElements = [...block.elements];
    const [draggedElement] = newElements.splice(draggedElementIndex, 1);
    newElements.splice(targetIndex, 0, draggedElement);
    onUpdateBlock(blockIndex, { ...block, elements: newElements });
    setDraggedElementIndex(null);
  };

  return (
    <div
      data-block-id={block.id}
      className={`mb-2 rounded-lg transition-all ${
        isDragging ? 'opacity-50 bg-white/5' : ''
      }`}
      draggable
      onDragStart={(e) => onDragStart(e, blockIndex)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, blockIndex)}
    >
      {/* Block Header (Toggle) */}
      <div
        className="flex items-center gap-2 py-2 ps-0 pe-2 sm:px-2 rounded-lg sm:hover:bg-white/5 cursor-pointer group"
        onClick={(e) => {
          // Only toggle if clicking outside the title input
          if (e.target === e.currentTarget || e.target.closest('button') || e.target.closest('.sum-display')) {
            isTogglingRef.current = true;
            handleToggle();
          }
        }}
      >
        {/* Toggle Arrow */}
        <button
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent blur on title input
            isTogglingRef.current = true;
          }}
          onClick={handleToggle}
          className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
        >
          {isOpen ? (
            <ChevronDownIcon className="w-5 h-5 sm:w-4 sm:h-4" />
          ) : (
            <ChevronRightIcon className="w-5 h-5 sm:w-4 sm:h-4" />
          )}
        </button>

        {/* Title Input */}
        <input
          ref={titleRef}
          type="text"
          value={block.title}
          onChange={(e) => onUpdateBlock(blockIndex, { ...block, title: e.target.value })}
          onKeyDown={handleTitleKeyDown}
          onBlur={handleTitleBlur}
          placeholder="New group..."
          className="flex-1 min-w-0 max-w-[60%] bg-transparent text-white placeholder-gray-500 outline-none font-bold"
        />

        {/* Sum Display */}
        <span className="sum-display text-[#BD93D8] shrink-0 text-right min-w-[35%] font-bold">
          {formatNumber(blockSum)}
        </span>
      </div>

      {/* Elements (Expanded Content) */}
      {isOpen && (
        <div className="ml-8 pl-2 border-l border-gray-700/50 mt-1 mb-2">
          {block.elements.map((element, index) => (
            <SumElement
              key={element.id}
              element={element}
              index={index}
              onUpdate={handleUpdateElement}
              onDelete={handleDeleteElement}
              onEnterFromName={handleEnterFromName}
              onEnterFromValue={handleEnterFromValue}
              onBackspace={handleBackspaceElement}
              onArrowDown={handleArrowDownFromElement}
              onArrowUp={handleArrowUpFromElement}
              nameRef={(el) => (elementNameRefs.current[index] = el)}
              valueRef={(el) => (elementValueRefs.current[index] = el)}
              onDragStart={handleElementDragStart}
              onDragOver={handleElementDragOver}
              onDrop={handleElementDrop}
              isDragging={draggedElementIndex === index}
            />
          ))}
        </div>
      )}
    </div>
  );
});

// Main SumSum Component
const SumSum = forwardRef((props, ref) => {
  const { user } = useAuth();
  const [documentId, setDocumentId] = useState(null);
  const [title, setTitle] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const titleRef = useRef(null);
  const blockTitleRefs = useRef([]);
  const blockRefs = useRef([]);
  const [blockOpenStates, setBlockOpenStates] = useState({});
  const [draggedBlockIndex, setDraggedBlockIndex] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved'
  const [isDirty, setIsDirty] = useState(false); // Track if user has made changes
  const saveTimeoutRef = useRef(null);
  const lastSavedRef = useRef(null);
  const documentListenerRef = useRef(null); // Store unsubscribe function for real-time listener
  const isSavingRef = useRef(false); // Track if we're currently saving to prevent conflict
  const isDirtyRef = useRef(false); // Ref version of isDirty for synchronous access

  // Helper to update isDirty and its ref together
  const updateIsDirty = useCallback((value) => {
    setIsDirty(value);
    isDirtyRef.current = value;
  }, []);

  // Helper to load a document by ID with real-time streaming
  const loadDocumentById = useCallback((docId) => {
    if (!docId || !user) return false;

    // Clean up any existing listener
    if (documentListenerRef.current) {
      documentListenerRef.current();
      documentListenerRef.current = null;
    }

    try {
      // Set up real-time listener
      const unsubscribe = onSnapshot(
        doc(db, 'documents', docId),
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();

            // Verify this document belongs to the current user
            if (data.userId !== user.uid) return;

            // Don't update if we're currently saving or have unsaved changes (prevents overwriting our own changes)
            if (isSavingRef.current || isDirtyRef.current) return;

            setDocumentId(docId);
            setTitle(data.title || '');
            const loadedBlocks = (data.blocks || []).map(block => ({
              id: generateId(),
              title: block.title || '',
              elements: (block.elements || []).map(el => ({
                id: generateId(),
                name: el.name || '',
                value: el.value || ''
              }))
            }));
            setBlocks(loadedBlocks);
            setHasInitialized(loadedBlocks.length > 0);
            updateIsDirty(false);
            lastSavedRef.current = JSON.stringify({ title: data.title || '', blocks: (data.blocks || []).map(b => ({ title: b.title, elements: b.elements.map(e => ({ name: e.name, value: e.value })) })) });
            setSaveStatus(null);

            // Update last opened document in localStorage
            localStorage.setItem(LAST_OPENED_KEY, docId);
          }
        },
        (error) => {
          console.error('Error listening to document:', error);
        }
      );

      // Store unsubscribe function
      documentListenerRef.current = unsubscribe;
      return true;
    } catch (error) {
      console.error('Error setting up document listener:', error);
      return false;
    }
  }, [user, updateIsDirty]);

  // Load most recently edited document for the user
  const loadMostRecentDocument = useCallback(async () => {
    if (!user) return false;

    try {
      const q = query(
        collection(db, 'documents'),
        where('userId', '==', user.uid),
        orderBy('updatedAt', 'desc'),
        limit(1)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const docSnap = snapshot.docs[0];
        const data = docSnap.data();

        setDocumentId(docSnap.id);
        setTitle(data.title || '');
        const loadedBlocks = (data.blocks || []).map(block => ({
          id: generateId(),
          title: block.title || '',
          elements: (block.elements || []).map(el => ({
            id: generateId(),
            name: el.name || '',
            value: el.value || ''
          }))
        }));
        setBlocks(loadedBlocks);
        setHasInitialized(loadedBlocks.length > 0);
        updateIsDirty(false);
        lastSavedRef.current = JSON.stringify({ title: data.title || '', blocks: (data.blocks || []).map(b => ({ title: b.title, elements: b.elements.map(e => ({ name: e.name, value: e.value })) })) });
        setSaveStatus(null);
        localStorage.setItem(LAST_OPENED_KEY, docSnap.id);
        return true;
      }
    } catch (error) {
      console.error('Error loading most recent document:', error);
    }
    return false;
  }, [user, updateIsDirty]);

  // Load last opened document on startup (for logged in users)
  useEffect(() => {
    if (!user) return;

    const loadInitialDocument = async () => {
      // Check if there's a pending copy from shared document viewer
      const copyData = sessionStorage.getItem('copySnapshot');
      if (copyData) {
        // Don't load last document - the copy will be loaded by another effect
        return;
      }

      const lastOpenedId = localStorage.getItem(LAST_OPENED_KEY);
      if (lastOpenedId) {
        const loaded = await loadDocumentById(lastOpenedId);
        if (!loaded) {
          // Last opened doc doesn't exist anymore, try most recent
          await loadMostRecentDocument();
        }
      } else {
        // No last opened, load most recent
        await loadMostRecentDocument();
      }
    };

    loadInitialDocument();
  }, [user, loadDocumentById, loadMostRecentDocument]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    resetDocument: () => {
      // Clean up listener
      if (documentListenerRef.current) {
        documentListenerRef.current();
        documentListenerRef.current = null;
      }
      setDocumentId(null);
      setTitle('');
      setBlocks([]);
      setHasInitialized(false);
      updateIsDirty(false); // Reset dirty flag
      lastSavedRef.current = null;
      setSaveStatus(null);
      localStorage.removeItem(LAST_OPENED_KEY); // Clear last opened
      setTimeout(() => titleRef.current?.focus(), 10);
    },
    loadTemplate: (template) => {
      // Clean up listener
      if (documentListenerRef.current) {
        documentListenerRef.current();
        documentListenerRef.current = null;
      }
      setDocumentId(null);
      setTitle(template.name);
      const newBlocks = template.blocks.map(block => ({
        id: generateId(),
        title: block.title,
        elements: block.elements.map(el => ({
          id: generateId(),
          name: el.name,
          value: el.value
        }))
      }));
      setBlocks(newBlocks);
      setHasInitialized(newBlocks.length > 0);
      updateIsDirty(false); // Not dirty until user edits
      lastSavedRef.current = null;
      setSaveStatus(null);
      localStorage.removeItem(LAST_OPENED_KEY); // Clear last opened
      setTimeout(() => titleRef.current?.focus(), 10);
    },
    loadDocument: (document) => {
      // Set up real-time listener for this document
      loadDocumentById(document.id);
    },
    getDocumentData: () => {
      // Return current document data for sharing
      return {
        title,
        blocks: blocks.map(block => ({
          title: block.title,
          elements: block.elements.map(el => ({
            name: el.name,
            value: el.value
          }))
        }))
      };
    },
    getCurrentDocumentId: () => {
      return documentId;
    }
  }), [updateIsDirty, loadDocumentById, title, blocks, documentId]);

  // Duplicate current document
  const handleDuplicateDocument = async () => {
    if (!user) {
      toast.error('Please log in to duplicate notes');
      return;
    }

    try {
      const newDocId = `doc_${Date.now()}`;
      const docData = {
        userId: user.uid,
        title: `${title || 'Heading'} (Copy)`,
        blocks: blocks.map(block => ({
          title: block.title,
          elements: block.elements.map(el => ({
            name: el.name,
            value: el.value
          }))
        })),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      };

      await setDoc(doc(db, 'documents', newDocId), docData);

      // Load the new duplicated document
      loadDocumentById(newDocId);
      toast.success('Note duplicated');
    } catch (error) {
      console.error('Error duplicating document:', error);
      toast.error('Failed to duplicate note');
    }
  };

  // Delete current document
  const handleDeleteDocument = async () => {
    if (!user) {
      toast.error('Please log in to delete notes');
      return;
    }

    if (!documentId) {
      toast.error('No note to delete');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'documents', documentId));
      toast.success('Note deleted');

      // Load the most recent document or create new one
      const q = query(
        collection(db, 'documents'),
        where('userId', '==', user.uid),
        orderBy('updatedAt', 'desc'),
        limit(1)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Load the most recent document
        const mostRecentDoc = querySnapshot.docs[0];
        loadDocumentById(mostRecentDoc.id);
      } else {
        // No documents left, reset to new document
        setDocumentId(null);
        setTitle('');
        const newBlock = {
          id: generateId(),
          title: '',
          elements: [{ id: generateId(), name: '', value: '' }],
        };
        setBlocks([newBlock]);
        setHasInitialized(false);
        updateIsDirty(false);
        localStorage.removeItem(LAST_OPENED_KEY);
        setTimeout(() => titleRef.current?.focus(), 10);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete note');
    }
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to log out');
    }
  };

  // Create a hash of current document state for comparison
  const getDocumentHash = useCallback(() => {
    // Filter out empty blocks (empty title and all elements are empty)
    const nonEmptyBlocks = blocks.filter(block => {
      const hasTitle = block.title.trim() !== '';
      const hasNonEmptyElements = block.elements.some(el =>
        el.name.trim() !== '' || el.value.toString().trim() !== ''
      );
      return hasTitle || hasNonEmptyElements;
    });

    return JSON.stringify({
      title,
      blocks: nonEmptyBlocks.map(b => ({
        title: b.title,
        elements: b.elements.map(e => ({ name: e.name, value: e.value }))
      }))
    });
  }, [title, blocks]);

  // Save function
  const saveDocument = useCallback(async () => {
    if (!user || !hasInitialized) return;

    const currentHash = getDocumentHash();

    // Skip if nothing changed
    if (lastSavedRef.current === currentHash) return;

    // Prevent multiple simultaneous saves
    if (isSavingRef.current) return;

    setSaveStatus('saving');
    isSavingRef.current = true; // Mark that we're saving

    try {
      const docId = documentId || `doc_${Date.now()}`;
      const isNewDocument = !documentId;

      // Filter out empty blocks before saving
      const nonEmptyBlocks = blocks.filter(block => {
        const hasTitle = block.title.trim() !== '';
        const hasNonEmptyElements = block.elements.some(el =>
          el.name.trim() !== '' || el.value.toString().trim() !== ''
        );
        return hasTitle || hasNonEmptyElements;
      });

      const docData = {
        userId: user.uid,
        title: title || 'Heading',
        blocks: nonEmptyBlocks.map(block => ({
          title: block.title,
          elements: block.elements.map(el => ({
            name: el.name,
            value: el.value
          }))
        })),
        updatedAt: serverTimestamp(),
        createdAt: isNewDocument ? serverTimestamp() : undefined
      };

      // Remove undefined fields
      Object.keys(docData).forEach(key => docData[key] === undefined && delete docData[key]);

      await setDoc(doc(db, 'documents', docId), docData, { merge: true });

      if (isNewDocument) {
        // New document created - set up listener for it
        setDocumentId(docId);
        localStorage.setItem(LAST_OPENED_KEY, docId);

        // Set up real-time listener for the newly created document
        setTimeout(() => {
          loadDocumentById(docId);
          isSavingRef.current = false;
        }, 500);
      } else {
        // Existing document - allow listener to update after a short delay
        setTimeout(() => {
          isSavingRef.current = false;
        }, 500);
      }

      lastSavedRef.current = currentHash;
      setSaveStatus('saved');

      // Clear "saved" status after 2 seconds
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error('Error saving document:', error);
      setSaveStatus(null);
      isSavingRef.current = false; // Reset on error
    }
  }, [user, hasInitialized, documentId, title, blocks, getDocumentHash, loadDocumentById]);

  // Auto-save document when changes occur (debounced 1.5 seconds after last change)
  useEffect(() => {
    if (!user || !hasInitialized || !isDirty) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save (500ms for quick saves)
    saveTimeoutRef.current = setTimeout(saveDocument, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [user, title, blocks, hasInitialized, isDirty, saveDocument]);

  // Save when page loses visibility (switching tabs, closing, etc.)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && user && hasInitialized && isDirty) {
        const currentHash = getDocumentHash();
        if (lastSavedRef.current !== currentHash) {
          saveDocument();
        }
      }
    };

    const handleBeforeUnload = (e) => {
      if (!hasInitialized || !isDirty) return;

      const currentHash = getDocumentHash();
      const hasUnsavedChanges = lastSavedRef.current !== currentHash;

      if (hasUnsavedChanges) {
        if (user) {
          // Logged in: trigger save
          saveDocument();
        } else {
          // Not logged in: warn about unsaved changes
          e.preventDefault();
          e.returnValue = 'You have unsaved changes. Log in to save them.';
          return e.returnValue;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user, hasInitialized, isDirty, getDocumentHash, saveDocument]);

  // Auto-save when user logs in with unsaved changes
  useEffect(() => {
    if (user && hasInitialized && isDirty && !documentId) {
      // User just logged in with unsaved changes - save as new document
      saveDocument();
    }
  }, [user, hasInitialized, isDirty, documentId, saveDocument]);

  // Cleanup: Unsubscribe from document listener when component unmounts
  useEffect(() => {
    return () => {
      if (documentListenerRef.current) {
        documentListenerRef.current();
        documentListenerRef.current = null;
      }
    };
  }, []);

  // Check for copied snapshot from shared document viewer
  useEffect(() => {
    const copyData = sessionStorage.getItem('copySnapshot');
    if (copyData) {
      try {
        const snapshot = JSON.parse(copyData);
        // Clean up listener
        if (documentListenerRef.current) {
          documentListenerRef.current();
          documentListenerRef.current = null;
        }

        setDocumentId(null); // This will be a new document
        setTitle(snapshot.title || '');
        const loadedBlocks = (snapshot.blocks || []).map(block => ({
          id: generateId(),
          title: block.title || '',
          elements: (block.elements || []).map(el => ({
            id: generateId(),
            name: el.name || '',
            value: el.value || ''
          }))
        }));
        setBlocks(loadedBlocks);
        setHasInitialized(loadedBlocks.length > 0);
        updateIsDirty(user ? true : false); // Only mark as dirty if user is logged in
        lastSavedRef.current = null;
        setSaveStatus(null);

        // Clear from sessionStorage
        sessionStorage.removeItem('copySnapshot');
      } catch (error) {
        console.error('Error loading copied snapshot:', error);
      }
    }
  }, [user]); // Re-run when user changes (logs in)

  // Calculate grand total
  const grandTotal = blocks.reduce((total, block) => {
    return total + block.elements.reduce((sum, el) => sum + parseNumber(el.value), 0);
  }, 0);

  // Initialize with focus on title
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  // Find index of first empty block (empty title)
  const findEmptyBlockIndex = () => {
    return blocks.findIndex(block => block.title === '');
  };

  // Handle arrow down from main title
  const handleArrowDownFromTitle = () => {
    if (blocks.length > 0) {
      blockTitleRefs.current[0]?.focus();
    }
  };

  // Handle arrow down from block title
  const handleArrowDownFromBlockTitle = (blockIndex) => {
    const block = blocks[blockIndex];
    const isBlockOpen = blockOpenStates[block.id];

    if (isBlockOpen && block.elements.length > 0) {
      // Block is open, navigate to first element's name field
      const blockRef = blockRefs.current[blockIndex];
      const elementNameRefs = blockRef?.getElementNameRefs?.();
      if (elementNameRefs?.[0]) {
        elementNameRefs[0].focus();
      }
    } else {
      // Block is closed or has no elements, navigate to next block
      if (blockIndex < blocks.length - 1) {
        blockTitleRefs.current[blockIndex + 1]?.focus();
      }
    }
  };

  // Handle arrow up from block title
  const handleArrowUpFromBlockTitle = (blockIndex) => {
    if (blockIndex > 0) {
      const prevBlock = blocks[blockIndex - 1];
      const isPrevBlockOpen = blockOpenStates[prevBlock.id];

      if (isPrevBlockOpen && prevBlock.elements.length > 0) {
        // Previous block is open, navigate to last element's value field
        const lastElementIndex = prevBlock.elements.length - 1;
        const prevBlockRef = blockRefs.current[blockIndex - 1];
        const elementValueRefs = prevBlockRef?.getElementValueRefs?.();
        if (elementValueRefs?.[lastElementIndex]) {
          elementValueRefs[lastElementIndex].focus();
        }
      } else {
        // Previous block is closed, navigate to its title
        blockTitleRefs.current[blockIndex - 1]?.focus();
      }
    } else {
      // First block, navigate to main title
      titleRef.current?.focus();
    }
  };

  // Handle arrow down from element (value field)
  const handleArrowDownFromElement = (blockIndex, elementIndex) => {
    const block = blocks[blockIndex];

    if (elementIndex < block.elements.length - 1) {
      // Navigate to next element's name field
      const blockRef = blockRefs.current[blockIndex];
      const elementNameRefs = blockRef?.getElementNameRefs?.();
      if (elementNameRefs?.[elementIndex + 1]) {
        elementNameRefs[elementIndex + 1].focus();
      }
    } else {
      // Last element in block, navigate to next block's title
      if (blockIndex < blocks.length - 1) {
        blockTitleRefs.current[blockIndex + 1]?.focus();
      }
    }
  };

  // Handle arrow up from element (name field)
  const handleArrowUpFromElement = (blockIndex, elementIndex) => {
    if (elementIndex > 0) {
      // Navigate to previous element's value field
      const blockRef = blockRefs.current[blockIndex];
      const elementValueRefs = blockRef?.getElementValueRefs?.();
      if (elementValueRefs?.[elementIndex - 1]) {
        elementValueRefs[elementIndex - 1].focus();
      }
    } else {
      // First element in block, navigate to block title
      blockTitleRefs.current[blockIndex]?.focus();
    }
  };

  // Handle block toggle
  const handleBlockToggle = (blockIndex, isOpen) => {
    const block = blocks[blockIndex];
    setBlockOpenStates(prev => ({
      ...prev,
      [block.id]: isOpen
    }));
  };

  // Handle title keydown
  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!hasInitialized) {
        // Create first block
        const newBlock = {
          id: generateId(),
          title: '',
          elements: [{ id: generateId(), name: '', value: '' }],
        };
        setBlocks([newBlock]);
        setHasInitialized(true);
        setTimeout(() => {
          blockTitleRefs.current[0]?.focus();
        }, 10);
      } else if (blocks.length > 0) {
        // Focus first block
        blockTitleRefs.current[0]?.focus();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleArrowDownFromTitle();
    }
  };

  // Handle title blur
  const handleTitleBlur = () => {
    if (!hasInitialized && title !== '') {
      // Check if there's already an empty block (shouldn't happen if not initialized, but just in case)
      const emptyBlockIndex = findEmptyBlockIndex();
      if (emptyBlockIndex !== -1) {
        setTimeout(() => {
          blockTitleRefs.current[emptyBlockIndex]?.focus();
        }, 10);
        return;
      }

      // Create first block when clicking away from title
      const newBlock = {
        id: generateId(),
        title: '',
        elements: [{ id: generateId(), name: '', value: '' }],
      };
      setBlocks([newBlock]);
      setHasInitialized(true);
    }
  };

  // Update block
  const handleUpdateBlock = useCallback((index, updatedBlock) => {
    setBlocks((prev) => {
      const newBlocks = [...prev];
      newBlocks[index] = updatedBlock;
      return newBlocks;
    });
    updateIsDirty(true);
  }, [updateIsDirty]);

  // Delete block
  const handleDeleteBlock = (index) => {
    if (blocks.length === 1) {
      // Reset to single empty block
      setBlocks([{
        id: generateId(),
        title: '',
        elements: [{ id: generateId(), name: '', value: '' }],
      }]);
      updateIsDirty(true);
      return;
    }
    setBlocks((prev) => prev.filter((_, i) => i !== index));
    updateIsDirty(true);

    // Focus previous block or title
    setTimeout(() => {
      if (index > 0) {
        blockTitleRefs.current[index - 1]?.focus();
      } else {
        titleRef.current?.focus();
      }
    }, 10);
  };

  // Add new block after specific index (or focus existing empty block)
  const addBlockAfter = (afterIndex) => {
    // Check if there's already an empty block
    const emptyBlockIndex = findEmptyBlockIndex();
    if (emptyBlockIndex !== -1) {
      // Focus the existing empty block instead of creating a new one
      setTimeout(() => {
        blockTitleRefs.current[emptyBlockIndex]?.focus();
      }, 10);
      return;
    }

    const newBlock = {
      id: generateId(),
      title: '',
      elements: [{ id: generateId(), name: '', value: '' }],
    };
    setBlocks((prev) => {
      const newBlocks = [...prev];
      newBlocks.splice(afterIndex + 1, 0, newBlock);
      return newBlocks;
    });

    // Focus new block's title
    setTimeout(() => {
      blockTitleRefs.current[afterIndex + 1]?.focus();
    }, 10);
  };

  // Handle Enter from block title -> create new block
  const handleEnterFromBlockTitle = (blockIndex) => {
    addBlockAfter(blockIndex);
  };

  // Handle backspace from empty block title
  const handleBackspaceFromTitle = (blockIndex) => {
    handleDeleteBlock(blockIndex);
  };

  // Block drag and drop
  const handleBlockDragStart = (e, index) => {
    setDraggedBlockIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleBlockDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleBlockDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedBlockIndex === null || draggedBlockIndex === targetIndex) {
      setDraggedBlockIndex(null);
      return;
    }

    setBlocks((prev) => {
      const newBlocks = [...prev];
      const [draggedBlock] = newBlocks.splice(draggedBlockIndex, 1);
      newBlocks.splice(targetIndex, 0, draggedBlock);
      return newBlocks;
    });
    setDraggedBlockIndex(null);
    updateIsDirty(true);
  };

  // Handle click outside blocks to add new block
  const handleContainerClick = (e) => {
    if (e.target === e.currentTarget) {
      // Check if there are any filled blocks
      const hasFilledBlocks = blocks.some(block => {
        const hasTitle = block.title.trim() !== '';
        const hasNonEmptyElements = block.elements.some(el =>
          el.name.trim() !== '' || el.value.toString().trim() !== ''
        );
        return hasTitle || hasNonEmptyElements;
      });

      // If no filled blocks, focus the main title
      if (!hasFilledBlocks) {
        titleRef.current?.focus();
        return;
      }

      // Otherwise, if initialized, add new block at the end
      if (hasInitialized) {
        addBlockAfter(blocks.length - 1);
      }
    }
  };

  return (
    <div className="flex flex-col items-center h-full w-full">
      <div className="relative w-full md:max-w-[800px] h-full flex-1">
        {/* Editor Container */}
        <div className="relative rounded-none md:rounded-[50px]">
          <div
            className="w-full min-h-screen bg-[#1b1e1c] mb-24 px-5 pt-5 pb-24 md:p-12 rounded-none md:rounded-[50px] cursor-text relative overflow-hidden"
            onClick={handleContainerClick}
          >
            {/* Content */}
            <div className="relative z-10 flex flex-col w-full max-w-full">
              {/* Title row with save indicator and menu */}
              <div className="flex items-center gap-2">
                <input
                  ref={titleRef}
                  type="text"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); updateIsDirty(true); }}
                  onKeyDown={handleTitleKeyDown}
                  onBlur={handleTitleBlur}
                  spellCheck={false}
                  placeholder="Heading"
                  className="flex-1 min-w-0 bg-transparent text-[#7BCDBA] text-2xl font-bold placeholder-[#7BCDBA]/20 outline-none border-b-2 border-transparent py-2"
                />
                {/* Save status indicator */}
                {user && saveStatus && (
                  <div className="shrink-0 flex items-center justify-center w-4 h-4">
                    {saveStatus === 'saving' ? (
                      <div className="w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-[#7BCDBA]" />
                    )}
                  </div>
                )}
                {/* Dropdown Menu */}
                <DocumentMenu
                  onDuplicate={handleDuplicateDocument}
                  onDelete={handleDeleteDocument}
                  onShare={() => {
                    if (window.triggerShare) {
                      window.triggerShare();
                    }
                  }}
                  user={user}
                  onLogout={handleLogout}
                />
              </div>

              {/* Sum Blocks */}
              <div className="flex flex-col">
                {blocks.map((block, index) => (
                  <SumBlock
                    key={block.id}
                    ref={(el) => (blockRefs.current[index] = el)}
                    block={block}
                    blockIndex={index}
                    onUpdateBlock={handleUpdateBlock}
                    onDeleteBlock={handleDeleteBlock}
                    onEnterFromTitle={handleEnterFromBlockTitle}
                    onBackspaceFromTitle={handleBackspaceFromTitle}
                    onArrowDownFromTitle={handleArrowDownFromBlockTitle}
                    onArrowUpFromTitle={handleArrowUpFromBlockTitle}
                    onArrowDownFromElement={handleArrowDownFromElement}
                    onArrowUpFromElement={handleArrowUpFromElement}
                    titleRef={(el) => (blockTitleRefs.current[index] = el)}
                    onDragStart={handleBlockDragStart}
                    onDragOver={handleBlockDragOver}
                    onDrop={handleBlockDrop}
                    isDragging={draggedBlockIndex === index}
                    isOpen={blockOpenStates[block.id]}
                    onToggle={handleBlockToggle}
                  />
                ))}
              </div>

              {/* Total Display - positioned after last block */}
              {hasInitialized && (
                <div className="mt-4 pt-3 border-t border-white/10">
                  <div className="flex justify-end items-center px-2 py-1">
                    <span className="text-[#7BCDBA] font-bold w-28 text-right">
                      {formatNumber(grandTotal)}
                    </span>
                  </div>
                </div>
              )}

              {/* Add sum-block button */}
              <button
                onClick={() => {
                  if (hasInitialized) {
                    addBlockAfter(blocks.length - 1);
                  } else {
                    // Create first block if not initialized
                    const newBlock = {
                      id: generateId(),
                      title: '',
                      elements: [{ id: generateId(), name: '', value: '' }],
                    };
                    setBlocks([newBlock]);
                    setHasInitialized(true);
                  }
                }}
                className="mt-4 w-full py-2 px-4 text-gray-500 hover:text-gray-300 text-sm flex items-center justify-center gap-2 rounded-full hover:bg-transparent sm:hover:bg-white/5 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                New sum group
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default SumSum;
